'use strict';

/**
 * Runs a standalone Stripe 3DS authentication when CardPointe demands one, then completes the pending order.
 * Shared by checkout and the payment-update page; only what happens after completion differs between them.
 *
 * The bank's challenge is Stripe's to render; our job is the sequence:
 *   card + CVV in the modal → Stripe PaymentMethod → server creates the authentication
 *   → (issuer fingerprinting, if offered) → (challenge, if required) → server verifies and re-submits to CardPointe.
 */
angular.module('360StoreAngularApp')
	.factory('stripe3ds', function ($rootScope, $timeout, $uibModal, api, BUILD_DATE) {

		/** The browser facts the 3DS protocol wants; the server adds the Accept header and the IP address. */
		function browserChannel() {
			return {
				javaEnabled: !!(navigator.javaEnabled && navigator.javaEnabled()),
				language: (navigator.languages && navigator.languages[0]) || navigator.language || 'en-US',
				colorDepth: window.screen.colorDepth,
				screenHeight: window.screen.height,
				screenWidth: window.screen.width,
				timezoneOffset: new Date().getTimezoneOffset(),
				userAgent: navigator.userAgent,
			};
		}

		/** Create → fingerprint → submit → challenge, ending when the authentication is in a final state. */
		async function authenticate(stripe, cardElement, cartId) {
			const pm = await stripe.createPaymentMethod({type: 'card', card: cardElement});
			if (pm.error) {
				throw new Error(pm.error.message);
			}
			let auth = await api.stripe3DSAuthenticate({cartId: cartId, paymentMethodId: pm.paymentMethod.id, browser: browserChannel()});
			if (auth.status === 'requires_submission') {
				let fingerprintingResult = null;
				if (auth.fingerprintingUrl) {
					fingerprintingResult = (await stripe.performThreeDSecureFingerprinting(auth.fingerprintingUrl)).fingerprintingResult;
				}
				auth = await api.stripe3DSSubmit({authenticationId: auth.authenticationId, fingerprintingResult: fingerprintingResult});
			}
			if (auth.status === 'requires_challenge') {
				await stripe.performThreeDSecureChallenge(auth.challengeUrl); // resolves when the cardholder finishes or gives up; the server checks the real result
			}
			return auth.authenticationId;
		}

		return {
			/**
			 * @param {Stripe3DSChallenge} challenge what the server returned: cartId, stripePublishableKey
			 * @return {Promise<CheckoutResponse>} the completed order. Rejects with 'cancel' if the customer backs out,
			 *         'stripe-not-loaded' if Stripe.js is unavailable.
			 */
			challenge: function (challenge) {
				const modalScope = $rootScope.$new(true);
				modalScope.stripe3DS = {submitting: false, errorMessage: null, cvv: '', submit: null}; // submit is set once Stripe is ready

				const modal = $uibModal.open({
					templateUrl: 'views/modal-stripe-3ds.html?v=' + BUILD_DATE,
					scope: modalScope,
					backdrop: 'static', // no accidental dismiss mid-challenge
					size: 'md',
					keyboard: false,
				});

				modal.opened.then(function () {
					if (typeof Stripe === 'undefined') {
						toastr.error('Stripe.js failed to load. Please refresh and try again.');
						modal.dismiss('stripe-not-loaded');
						return;
					}
					const stripe = Stripe(challenge.stripePublishableKey);
					const cardElement = stripe.elements().create('card');
					$timeout(function () { // after Angular has rendered the template, so the mount point exists
						cardElement.mount('#stripe-card-element');
					}, 0);

					modalScope.stripe3DS.submit = async function () {
						const cvv = (modalScope.stripe3DS.cvv || '').trim();
						if (!/^\d{3,4}$/.test(cvv)) {
							modalScope.stripe3DS.errorMessage = 'Please enter the 3- or 4-digit security code from the back of your card.';
							return;
						}
						modalScope.stripe3DS.submitting = true;
						modalScope.stripe3DS.errorMessage = null;
						try {
							const authenticationId = await authenticate(stripe, cardElement, challenge.cartId);
							const response = await api.checkout3DSStripeComplete({authenticationId: authenticationId, cartId: challenge.cartId, cvv2: cvv});
							modalScope.$applyAsync(function () { // native-Promise territory: hand the result back inside a digest
								modal.close(response);
							});
						} catch (err) {
							// native-Promise territory, so tell Angular ourselves
							modalScope.$applyAsync(function () {
								modalScope.stripe3DS.submitting = false;
								modalScope.stripe3DS.errorMessage = (err && err.headers && err.headers().proscerror) // server said why
									|| (err && err.message)
									|| 'Verification failed. Please call us at 770-234-9293 for help.';
							});
						}
					};
				});

				return modal.result;
			},
		};
	});
